import React, { useEffect, useState } from 'react';
import { AlertCircle, BarChart3, Coins, Hash, ArrowDownLeft, ArrowUpRight, MessageSquare, Zap, Users, Search, Database, FileText, GitBranch, Languages, Bot } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/common/Card';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts';
import { usageService, TotalUsage, UsageSummary, DailyUsage, AgentUsage, OperationUsage } from '@/services/usage.service';

const PROVIDER_COLORS: Record<string, string> = {
  openai: '#3B82F6',
  anthropic: '#F97316',
  gemini: '#10B981',
};

const PROVIDER_LABELS: Record<string, string> = {
  openai: 'OpenAI',
  anthropic: 'Anthropic',
  gemini: 'Gemini',
};

const OPERATION_LABELS: Record<string, string> = {
  chat_completion: 'Conversations',
  skill_extraction: 'Skill Extraction',
  simulation_bot: 'Training & Simulation',
  embedding: 'Embeddings',
  batch_embedding: 'Batch Embeddings',
  query_intelligence: 'Query Intelligence',
  document_generation: 'Doc Generation',
  document_extraction: 'Doc Extraction',
  graph_building: 'Knowledge Graph',
  field_extraction: 'Field Extraction',
  insight_generation: 'Insight Generation',
};

const OPERATION_COLORS: Record<string, string> = {
  chat_completion: '#3B82F6',
  skill_extraction: '#8B5CF6',
  simulation_bot: '#F59E0B',
  embedding: '#10B981',
  batch_embedding: '#06B6D4',
  query_intelligence: '#EC4899',
  document_generation: '#F97316',
  document_extraction: '#EF4444',
  graph_building: '#14B8A6',
  field_extraction: '#6366F1',
  insight_generation: '#A855F7',
};

const OPERATION_ICONS: Record<string, string> = {
  chat_completion: 'MessageSquare',
  skill_extraction: 'Zap',
  simulation_bot: 'Users',
  embedding: 'Search',
  batch_embedding: 'Database',
  query_intelligence: 'Languages',
  document_generation: 'FileText',
  document_extraction: 'FileText',
  graph_building: 'GitBranch',
  field_extraction: 'Bot',
  insight_generation: 'Zap',
};

const ICON_COMPONENTS: Record<string, React.FC<{ className?: string }>> = {
  MessageSquare,
  Zap,
  Users,
  Search,
  Database,
  Languages,
  FileText,
  GitBranch,
  Bot,
};

type DateRange = 7 | 30 | 90;

interface DailyChartData {
  date: string;
  input_tokens: number;
  output_tokens: number;
  total_tokens: number;
}

function formatCost(value: number): string {
  return `$${value.toFixed(2)}`;
}

function formatTokens(value: number): string {
  return value.toLocaleString();
}

function transformDailyData(daily: DailyUsage[]): DailyChartData[] {
  const byDate = new Map<string, DailyChartData>();

  for (const entry of daily) {
    const existing = byDate.get(entry.date);
    if (existing) {
      existing.input_tokens += entry.input_tokens;
      existing.output_tokens += entry.output_tokens;
      existing.total_tokens += entry.total_tokens;
    } else {
      byDate.set(entry.date, {
        date: entry.date,
        input_tokens: entry.input_tokens,
        output_tokens: entry.output_tokens,
        total_tokens: entry.total_tokens,
      });
    }
  }

  return Array.from(byDate.values()).sort(
    (a, b) => a.date.localeCompare(b.date)
  );
}

export const UsagePage: React.FC = () => {
  const [dateRange, setDateRange] = useState<DateRange>(30);
  const [total, setTotal] = useState<TotalUsage | null>(null);
  const [summary, setSummary] = useState<UsageSummary[]>([]);
  const [daily, setDaily] = useState<DailyUsage[]>([]);
  const [agents, setAgents] = useState<AgentUsage[]>([]);
  const [operations, setOperations] = useState<OperationUsage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsLoading(true);
        setError(null);

        const [totalData, summaryData, dailyData, agentData, operationData] = await Promise.all([
          usageService.getTotalUsage(),
          usageService.getUsageSummary(),
          usageService.getUsageDaily(dateRange),
          usageService.getUsageByAgent(),
          usageService.getUsageByOperation(),
        ]);

        setTotal(totalData);
        setSummary(summaryData);
        setDaily(dailyData);
        setAgents(agentData);
        setOperations(operationData);
      } catch (err) {
        console.error('Failed to fetch usage data:', err);
        setError('Failed to load usage data. Please try again.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [dateRange]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-12 h-12 rounded-xl bg-rose-500 flex items-center justify-center mx-auto mb-4 animate-pulse">
            <BarChart3 className="w-6 h-6 text-white" />
          </div>
          <p className="text-gray-500">Loading usage analytics...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="w-16 h-16 rounded-xl bg-red-100 flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
          <p className="text-lg font-semibold text-gray-900 mb-2">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="px-4 py-2 bg-rose-500 text-white font-semibold rounded-xl hover:bg-rose-600 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  const chartData = transformDailyData(daily);
  const providers = total?.by_provider ? Object.keys(total.by_provider) : [];

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">LLM Usage Analytics</h1>
          <p className="text-gray-500 mt-1">Monitor token consumption, costs, and usage patterns.</p>
        </div>
        <div className="flex items-center gap-2 bg-white rounded-xl border-2 border-[#1a1a2e] p-1">
          {([7, 30, 90] as DateRange[]).map((days) => (
            <button
              key={days}
              onClick={() => setDateRange(days)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                dateRange === days
                  ? 'bg-rose-500 text-white shadow-[3px_3px_0px_#1a1a2e]'
                  : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
              }`}
            >
              {days}d
            </button>
          ))}
        </div>
      </div>

      {/* Total Usage Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
        <Card className="bg-white rounded-xl border-2 border-[#1a1a2e]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Hash className="w-4 h-4" />
              Total Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {formatTokens(total?.total_tokens ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border-2 border-[#1a1a2e]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <ArrowDownLeft className="w-4 h-4" />
              Input Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {formatTokens(total?.total_input_tokens ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border-2 border-[#1a1a2e]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <ArrowUpRight className="w-4 h-4" />
              Output Tokens
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {formatTokens(total?.total_output_tokens ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border-2 border-[#1a1a2e]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <Coins className="w-4 h-4" />
              Estimated Cost
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {formatCost(total?.total_cost_usd ?? 0)}
            </p>
          </CardContent>
        </Card>

        <Card className="bg-white rounded-xl border-2 border-[#1a1a2e]">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
              <BarChart3 className="w-4 h-4" />
              Request Count
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-gray-900">
              {formatTokens(total?.request_count ?? 0)}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Usage by Category */}
      {operations.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage by Category</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-5">
            {operations.map((op) => {
              const color = OPERATION_COLORS[op.operation_type] ?? '#6B7280';
              const label = OPERATION_LABELS[op.operation_type] ?? op.operation_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
              const iconName = OPERATION_ICONS[op.operation_type];
              const IconComponent = iconName ? ICON_COMPONENTS[iconName] : null;

              return (
                <Card
                  key={op.operation_type}
                  className="bg-white rounded-xl border-2 border-[#1a1a2e] overflow-hidden"
                >
                  <div className="flex h-full">
                    <div className="w-1 shrink-0" style={{ backgroundColor: color }} />
                    <div className="flex-1">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                          {IconComponent && <IconComponent className="w-4 h-4" />}
                          {label}
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <div className="space-y-2">
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-gray-500">Tokens</span>
                            <span className="text-lg font-bold text-gray-900">
                              {formatTokens(op.total_tokens)}
                            </span>
                          </div>
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-gray-500">Cost</span>
                            <span className="text-sm font-bold" style={{ color }}>
                              {formatCost(op.total_cost_usd)}
                            </span>
                          </div>
                          <div className="flex justify-between items-baseline">
                            <span className="text-sm text-gray-500">Requests</span>
                            <span className="text-sm font-medium text-gray-700">
                              {formatTokens(op.request_count)}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}

      {/* Provider Breakdown Cards */}
      {providers.length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {providers.map((provider) => {
            const data = total!.by_provider[provider];
            const color = PROVIDER_COLORS[provider] ?? '#6B7280';
            const label = PROVIDER_LABELS[provider] ?? provider;

            return (
              <Card key={provider} className="bg-white rounded-xl border-2 border-[#1a1a2e]">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-gray-500 flex items-center gap-2">
                    <span
                      className="w-3 h-3 rounded-full inline-block"
                      style={{ backgroundColor: color }}
                    />
                    {label}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-gray-500">Total Tokens</span>
                      <span className="text-lg font-bold text-gray-900">
                        {formatTokens(data.total_tokens)}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-gray-500">Input</span>
                      <span className="text-sm font-medium text-gray-700">
                        {formatTokens(data.input_tokens)}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-gray-500">Output</span>
                      <span className="text-sm font-medium text-gray-700">
                        {formatTokens(data.output_tokens)}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline pt-2 border-t border-[#1a1a2e]">
                      <span className="text-sm text-gray-500">Cost</span>
                      <span className="text-lg font-bold" style={{ color }}>
                        {formatCost(data.cost_usd)}
                      </span>
                    </div>
                    <div className="flex justify-between items-baseline">
                      <span className="text-sm text-gray-500">Requests</span>
                      <span className="text-sm font-medium text-gray-700">
                        {formatTokens(data.request_count)}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Daily Usage Chart */}
      <Card className="bg-white rounded-xl border-2 border-[#1a1a2e]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">
            Daily Token Usage ({dateRange} days)
          </CardTitle>
        </CardHeader>
        <CardContent>
          {chartData.length === 0 ? (
            <div className="flex items-center justify-center h-64 text-gray-400">
              No daily usage data available for this period.
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={400}>
              <BarChart data={chartData} margin={{ top: 10, right: 30, left: 20, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E5E7EB" />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  tickFormatter={(value: string) => {
                    const d = new Date(value);
                    return `${d.getMonth() + 1}/${d.getDate()}`;
                  }}
                />
                <YAxis
                  tick={{ fontSize: 12, fill: '#6B7280' }}
                  tickFormatter={(value: number) => {
                    if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
                    if (value >= 1_000) return `${(value / 1_000).toFixed(0)}K`;
                    return String(value);
                  }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#fff',
                    border: '1px solid #E5E7EB',
                    borderRadius: '12px',
                    padding: '12px',
                  }}
                  formatter={(value: number, name: string) => [
                    formatTokens(value),
                    name === 'input_tokens' ? 'Input Tokens' : 'Output Tokens',
                  ]}
                  labelFormatter={(label: string) => {
                    const d = new Date(label);
                    return d.toLocaleDateString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      year: 'numeric',
                    });
                  }}
                />
                <Legend
                  formatter={(value: string) =>
                    value === 'input_tokens' ? 'Input Tokens' : 'Output Tokens'
                  }
                />
                <Bar
                  dataKey="input_tokens"
                  stackId="tokens"
                  fill="#3B82F6"
                  radius={[0, 0, 0, 0]}
                />
                <Bar
                  dataKey="output_tokens"
                  stackId="tokens"
                  fill="#F97316"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </CardContent>
      </Card>

      {/* Usage Summary Table */}
      <Card className="bg-white rounded-xl border-2 border-[#1a1a2e]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Usage Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {summary.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No usage summary data available.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1a1a2e]">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Provider</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Model</th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Operation</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Input Tokens</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Output Tokens</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Total</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Cost</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.map((row, i) => (
                    <tr
                      key={`${row.provider}-${row.model}-${row.operation_type}-${i}`}
                      className="border-b border-[#1a1a2e] hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4">
                        <span className="flex items-center gap-2">
                          <span
                            className="w-2.5 h-2.5 rounded-full inline-block"
                            style={{ backgroundColor: PROVIDER_COLORS[row.provider] ?? '#6B7280' }}
                          />
                          {PROVIDER_LABELS[row.provider] ?? row.provider}
                        </span>
                      </td>
                      <td className="py-3 px-4 font-mono text-xs text-gray-700">{row.model}</td>
                      <td className="py-3 px-4 text-gray-700">{OPERATION_LABELS[row.operation_type] ?? row.operation_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {formatTokens(row.total_input_tokens)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {formatTokens(row.total_output_tokens)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-gray-900">
                        {formatTokens(row.total_tokens)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {formatCost(row.total_cost_usd)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {formatTokens(row.request_count)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Per-Agent Usage Table */}
      <Card className="bg-white rounded-xl border-2 border-[#1a1a2e]">
        <CardHeader>
          <CardTitle className="text-lg font-semibold text-gray-900">Usage by Agent</CardTitle>
        </CardHeader>
        <CardContent>
          {agents.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No per-agent usage data available.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#1a1a2e]">
                    <th className="text-left py-3 px-4 font-semibold text-gray-600">Agent Name</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Input Tokens</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Output Tokens</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Total</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Cost</th>
                    <th className="text-right py-3 px-4 font-semibold text-gray-600">Requests</th>
                  </tr>
                </thead>
                <tbody>
                  {agents.map((agent) => (
                    <tr
                      key={agent.agent_id}
                      className="border-b border-[#1a1a2e] hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-4 font-medium text-gray-900">{agent.agent_name}</td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {formatTokens(agent.total_input_tokens)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {formatTokens(agent.total_output_tokens)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono font-semibold text-gray-900">
                        {formatTokens(agent.total_tokens)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {formatCost(agent.total_cost_usd)}
                      </td>
                      <td className="py-3 px-4 text-right font-mono text-gray-900">
                        {formatTokens(agent.request_count)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
